import { useAllowedAccess, useUser } from "@utilities/hooks";
import { UserShopRoleEnum } from "@utilities/types";
import { useCallback, useEffect, useMemo, useState } from "react";

const itemsPerPage = 5;

export const useControllers = () => {

    useAllowedAccess();
    const {
        user, createAccountError,
        collaborators, getCollaboratorsError,
        temporaryToken, setTemporaryToken,
        onCreateAccount, onGetOrganisationCollaborators,
    } = useUser();

    const [displayCollabModal, setDisplayCollabModal] = useState<boolean>(false);
    const [loadingCollabModal, setLoadingCollabModal] = useState<boolean>(false);
    const [loadingGetCollaborators, setLoadingGetCollaborators] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [serachCollaborator, setSearchCollaborator] = useState<string>('');
    const [selectedCollaboratorFilter, setSelectedCollaboratorFilter] = useState<string>('');

    const filteredCollaborators = useMemo(() => collaborators.filter((collaborator) => {
        const matchesSearch =
            (collaborator.firstname?.toLowerCase().includes(serachCollaborator.toLowerCase()) || false) ||
            (collaborator.lastname?.toLowerCase().includes(serachCollaborator.toLowerCase()) || false) ||
            (collaborator.email?.toLowerCase().includes(serachCollaborator.toLowerCase()) || false);

        const matchesRole =
            !selectedCollaboratorFilter || collaborator.role.toLowerCase() === selectedCollaboratorFilter.toLowerCase();

        return matchesSearch && matchesRole;
    }), [collaborators, selectedCollaboratorFilter, serachCollaborator]);

    const totalPages = useMemo(()=> 
        Math.ceil(filteredCollaborators.length / itemsPerPage), 
    [filteredCollaborators]);
    
    const paginatedCollaborators = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredCollaborators.slice(startIndex, endIndex);
    }, [filteredCollaborators, currentPage]);

    useEffect(()=> {
        handleGetCollaborators();
    }, []);

    useEffect(()=> {
        if(temporaryToken){
            setTemporaryToken('');
            onToggleCollabModal();
            handleGetCollaborators();
        }
    }, [temporaryToken]);

    const onChangePage = useCallback((page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    }, [totalPages]);

    const onToggleCollabModal = useCallback(() => {
        setDisplayCollabModal((prev) => !prev);
    }, []);

    const handleGetCollaborators = useCallback(async () => {
        setLoadingGetCollaborators(true);
        await onGetOrganisationCollaborators();
        setLoadingGetCollaborators(false);
    }, [onGetOrganisationCollaborators]);

    const onAddNewCollaborator = useCallback(async({email, role} : {email: string, role: UserShopRoleEnum})=> {
        if(email && role){
            setLoadingCollabModal(true);
            await onCreateAccount(email, role);
            setLoadingCollabModal(false);
        }
    }, [onCreateAccount]);

    return {
        user, currentPage, totalPages,
        paginatedCollaborators, getCollaboratorsError,
        loadingGetCollaborators, createAccountError,
        selectedCollaboratorFilter, setSelectedCollaboratorFilter,
        displayCollabModal, onToggleCollabModal,
        loadingCollabModal, onAddNewCollaborator,
        setSearchCollaborator, onChangePage,
    }
}
